/**
 * Navigation Hooks - React hooks for navigation
 *
 * These hooks provide React-compatible interfaces for navigation operations,
 * abstracting away the underlying routing implementation.
 */

import { useLocation as useUmiLocation, useHistory as useUmiHistory } from 'umi';
import { useMemo } from 'react';
import { NavigationLocation, navigationService } from '../services/navigation';

/**
 * Hook for navigation operations
 * Drop-in replacement for useHistory from react-router or umi
 */
export function useNavigation() {
  const umiHistory = useUmiHistory();

  return useMemo(() => ({
    push: (path: string | NavigationLocation) => navigationService.push(path),
    replace: (path: string | NavigationLocation) => navigationService.replace(path),
    go: (delta: number) => navigationService.go(delta),
    goBack: () => navigationService.back(),
    goForward: () => navigationService.forward(),

    // Legacy compatibility
    history: umiHistory,
  }), [umiHistory]);
}

/**
 * Hook for location information
 * Drop-in replacement for useLocation from react-router or umi
 */
export function useAppLocation() {
  const umiLocation = useUmiLocation();

  return useMemo(() => ({
    pathname: umiLocation.pathname,
    search: umiLocation.search,
    hash: umiLocation.hash,
    state: umiLocation.state,

    // Convenience getters
    query: navigationService.getQuery(),

    // Legacy compatibility
    location: umiLocation,
  }), [umiLocation]);
}

/**
 * Hook for query parameters
 * Provides easy access to URL search parameters
 */
export function useQueryParams() {
  const location = useAppLocation();

  return useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const params: Record<string, string> = {};

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    return params;
  }, [location.search]);
}

/**
 * Hook for programmatic navigation with convenience methods
 */
export function useRouter() {
  const navigation = useNavigation();
  const location = useAppLocation();

  return useMemo(() => ({
    // Navigation methods
    push: navigation.push,
    replace: navigation.replace,
    back: navigation.goBack,
    forward: navigation.goForward,

    // Location info
    pathname: location.pathname,
    query: location.query,

    // Convenience methods
    navigateTo: (path: string) => navigation.push(path),
    redirectTo: (path: string) => navigation.replace(path),
    navigateToHome: () => navigation.push('/home'),
    navigateToLogin: () => navigation.push('/login'),
    navigateToProfile: () => navigation.push('/profile'),
  }), [navigation, location]);
}

// Legacy exports for gradual migration
export { useUmiLocation as useLocation, useUmiHistory as useHistory };